import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createPaymentRequest } from '../reducers/payReducer';
import { handleSendMessage } from '../reducers/inboxReducer';
import * as PayHelpers from '../helpers/PayHelpers';
import { ValidationService, ValidationShapers } from '../services/ValidationService';
import { removePeriodsFromString } from '../helpers/StringHelpers';
import { isAndroid, isChromeBrowser } from '../helpers/BrowserHelpers';
import { organizationSelectors } from '../selectors';

const useRhinopayModal = (userId) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [paymentRequestAmount, setPaymentRequestAmount] = useState('');
  const [paymentRequestNote, setPaymentRequestNote] = useState('');
  const [errors, setErrors] = useState({});
  const [isFormInProgress, setIsFormInProgress] = useState(false);
  const activeOrg = useSelector((state) => organizationSelectors.getCurrentOrg(state));
  const organizationId = useSelector((state) => state.auth?.currentOrg);
  const paymentRequests = useSelector((state) => state.pay.paymentRequests);

  const reset = () => {
    setPaymentRequestAmount('');
    setPaymentRequestNote('');
    setErrors({});
    setIsFormInProgress(false);
  };

  const open = () => setIsOpen(true);

  const close = () => {
    reset();
    setIsOpen(false);
  };

  const handleChange = (name, value) => {
    if (name === 'paymentRequestNote') setPaymentRequestNote(value);
  };

  const handleFocus = (event) => {
    if (Number.isNaN(parseFloat(event.target.value))) {
      setPaymentRequestAmount('0.00');
    }
  };

  const shapeNumberToCurrency = (inputValue) => {
    if (Number.isNaN(parseFloat(inputValue))) return '0.00';
    return (parseFloat(inputValue) / 100).toFixed(2);
  };

  const getKeyValue = (keyCode) => {
    let kc = keyCode;
    if (kc > 57) kc -= 48;
    return (kc >= 48 && kc <= 57) ? String.fromCharCode(kc) : null;
  };

  const handleRequestAmountChange = (event) => {
    const DELETE_KEY_CODE = 8;
    const eventKey = event.which || event.keyCode;
    if (isAndroid() && isChromeBrowser()) {
      if (event.type === 'input') {
        setPaymentRequestAmount(shapeNumberToCurrency(removePeriodsFromString(event.target.value)));
      }
    } else if (event.type === 'keydown' && eventKey === DELETE_KEY_CODE && paymentRequestAmount.length > 0) {
      const trimmed = paymentRequestAmount.slice(0, paymentRequestAmount.length - 1);
      setPaymentRequestAmount(shapeNumberToCurrency(removePeriodsFromString(trimmed)));
      event.preventDefault();
    } else if (event.type === 'keyup' && eventKey !== DELETE_KEY_CODE && getKeyValue(eventKey)) {
      setPaymentRequestAmount(shapeNumberToCurrency(removePeriodsFromString(paymentRequestAmount) + getKeyValue(eventKey)));
    }
  };

  const handleSubmitRequest = async () => {
    const validationErrors = ValidationService(ValidationShapers.shapePaymentRequest({ paymentRequestAmount, paymentRequestNote }));
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsFormInProgress(true);
    try {
      const payload = {
        orgId: activeOrg.id,
        userId,
        requestAmount: parseFloat(paymentRequestAmount, 10),
        note: paymentRequestNote,
      };
      await dispatch(createPaymentRequest(payload));
      const paymentRequest = paymentRequests[userId];
      if (paymentRequest) {
        const messageText = PayHelpers.generatePaymentRequestMessageText(paymentRequest, activeOrg);
        const threadOptions = {
          userId,
          organizationId,
          assigned: false,
          following: false,
          direct: false,
          pageNo: 0,
          channelIds: [],
          initialPageNo: 0,
        };
        dispatch(handleSendMessage({ messageText, threadOptions }));
      }
      reset();
      setIsOpen(false);
    } catch (err) {
      reset();
      setIsOpen(false);
    }
  };

  return {
    isOpen,
    open,
    close,
    errors,
    isFormInProgress,
    paymentRequestAmount,
    paymentRequestNote,
    handleChange,
    handleFocus,
    handleRequestAmountChange,
    handleSubmitRequest,
  };
};

export default useRhinopayModal;
